import React from 'react';
import { Empty, Tag, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
	ILocationDiagram,
	IRackDetail,
	IShelfWithLocations,
} from '../../../../redux/api/inventory';
import { SLOT_TYPES, VOLUME_TYPES, WEIGHT_TYPES } from '../../constants/inventory';
import {
	buildSlotGrid,
	locationCapacityLabel,
	occupancyColor,
	shelfOccupancyPercent,
} from './rackVisual.helpers';
import SlotGrid from './SlotGrid';
import ShelfActions from './ShelfActions';

interface Props {
	rack: IRackDetail;
	canWrite: boolean;
	onLocationClick: (location: ILocationDiagram, shelf: IShelfWithLocations) => void;
	onAddLocation: (shelf: IShelfWithLocations, suggestedCode?: string) => void;
	onEditShelf: (shelf: IShelfWithLocations) => void;
	onDeleteShelf: (shelf: IShelfWithLocations) => void;
	onPermissionDenied: () => void;
	/** Uppercased location code to highlight (from the rack nav-bar search). */
	highlightCode?: string;
}

const LEVEL_HEIGHT = 56;
/** Offset so scrolled-to anchors clear the sticky rack header. */
const SCROLL_MARGIN = 140;

/**
 * Draws the rack as a physical bay: shelves stacked bottom-to-top, each level
 * rendered according to the rack's storage type (slots / shared band / bins).
 */
const RackVisual: React.FC<Props> = ({
	rack,
	canWrite,
	onLocationClick,
	onAddLocation,
	onEditShelf,
	onDeleteShelf,
	onPermissionDenied,
	highlightCode,
}) => {
	const isSlots = SLOT_TYPES.includes(rack.storage_type);
	const isBins = VOLUME_TYPES.includes(rack.storage_type);
	const isWeight = WEIGHT_TYPES.includes(rack.storage_type);

	if (!rack.shelves.length) {
		return (
			<Empty
				description="No shelves yet. Add a shelf or generate a structure."
				style={{ padding: 24 }}
			/>
		);
	}

	// Matrix layout (columns = code letter, rows = code number) for any rack
	// whose codes follow the letter+number convention — individual slots AND
	// shared bins render the same way. Falls back to the linear layout below
	// when codes aren't grid-shaped (e.g. shared shelves with a single code).
	const grid = buildSlotGrid(rack);
	if (grid) {
		return (
			<SlotGrid
				grid={grid}
				canWrite={canWrite}
				onLocationClick={onLocationClick}
				onAddLocation={onAddLocation}
				onEditShelf={onEditShelf}
				onDeleteShelf={onDeleteShelf}
				onPermissionDenied={onPermissionDenied}
				highlightCode={highlightCode}
			/>
		);
	}

	// Largest slot width across the rack → proportional slot sizing.
	const maxSlotWidth = Math.max(
		1,
		...rack.shelves.flatMap((s) => s.locations.map((l) => Number(l.max_width) || 0)),
	);

	const renderLocationCell = (
		loc: ILocationDiagram,
		shelf: IShelfWithLocations,
		options?: { grow?: number; full?: boolean },
	) => {
		const pct = isWeight ? shelfOccupancyPercent(shelf) : loc.occupancy_percent;
		const isHighlighted = !!highlightCode && loc.code.toUpperCase() === highlightCode;
		const tooltip = (
			<div>
				<div>
					<strong>{loc.code}</strong>
				</div>
				<div>Items: {loc.number_of_items}</div>
				<div>{isWeight ? 'Shelf occupancy (weight)' : 'Occupancy'}: {Math.round(pct)}%</div>
				{locationCapacityLabel(loc) && <div>{locationCapacityLabel(loc)}</div>}
			</div>
		);
		return (
			<Tooltip key={loc.id} title={tooltip}>
				<div
					data-slot-code={loc.code.toUpperCase()}
					onClick={() => onLocationClick(loc, shelf)}
					style={{
						flex: options?.full ? 1 : `${options?.grow ?? 1} 1 0`,
						minWidth: 54,
						height: LEVEL_HEIGHT - 12,
						background: occupancyColor(pct),
						border: isHighlighted ? '2px solid #1677ff' : '1px solid #d9d9d9',
						boxShadow: isHighlighted
							? '0 0 0 3px rgba(22,119,255,0.25)'
							: undefined,
						scrollMarginTop: SCROLL_MARGIN,
						borderRadius: 4,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer',
						fontSize: 12,
						lineHeight: 1.2,
						overflow: 'hidden',
						padding: '2px 4px',
					}}
				>
					<span style={{ fontWeight: 600 }}>{loc.code}</span>
					<span style={{ color: '#555' }}>{loc.number_of_items} items</span>
				</div>
			</Tooltip>
		);
	};

	const renderLevelContent = (shelf: IShelfWithLocations) => {
		if (!shelf.locations.length) {
			return (
				<div
					onClick={() => (canWrite ? onAddLocation(shelf) : onPermissionDenied())}
					style={{
						flex: 1,
						height: LEVEL_HEIGHT - 12,
						border: '1px dashed #d9d9d9',
						borderRadius: 4,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: '#999',
						cursor: 'pointer',
						fontSize: 12,
					}}
				>
					<PlusOutlined style={{ marginRight: 6 }} /> Add location
				</div>
			);
		}

		// Shared shelves → a single wide band representing the whole shelf.
		if (isWeight) {
			return renderLocationCell(shelf.locations[0], shelf, { full: true });
		}

		// Individual slots → discrete boxes sized proportionally to their width.
		if (isSlots) {
			return shelf.locations.map((loc) =>
				renderLocationCell(loc, shelf, {
					grow: (Number(loc.max_width) || maxSlotWidth) / maxSlotWidth,
				}),
			);
		}

		// Shared bins (and any other type) → equal-width cells.
		void isBins;
		return shelf.locations.map((loc) => renderLocationCell(loc, shelf));
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column-reverse', // Shelf 1 at the bottom
				gap: 6,
				background: '#fff',
				border: '2px solid #595959',
				borderRadius: 6,
				padding: 10,
			}}
		>
			{rack.shelves.map((shelf) => (
				<div
					key={shelf.id}
					style={{ display: 'flex', alignItems: 'center', gap: 10 }}
				>
					<div
						style={{
							flex: 1,
							display: 'flex',
							gap: 6,
							minHeight: LEVEL_HEIGHT - 12,
						}}
					>
						{renderLevelContent(shelf)}
					</div>
					<div
						style={{
							width: 170,
							flexShrink: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 8,
							fontSize: 12,
							color: '#595959',
						}}
						title={shelf.name}
					>
						<div>
							<div style={{ fontWeight: 600 }}>{shelf.name}</div>
							{(shelf.reservations_enabled === false ||
								rack.reservations_enabled === false) && (
								<Tag color="orange" style={{ marginTop: 4 }}>
									Reservations off
								</Tag>
							)}
							<div style={{ color: '#999', fontSize: 11 }}>
								{shelf.locations.length} locs ·{' '}
								{shelf.locations.reduce(
									(acc, loc) => acc + (loc.number_of_items || 0),
									0,
								)}{' '}
								items
							</div>
							{shelf.max_weight != null && (
								<div style={{ color: '#999', fontSize: 11 }}>
									{Math.round(shelf.occupied_weight || 0)}/{shelf.max_weight} lb ·{' '}
									{shelfOccupancyPercent(shelf)}% full
								</div>
							)}
						</div>
						<ShelfActions
							shelf={shelf}
							canWrite={canWrite}
							onEdit={onEditShelf}
							onDelete={onDeleteShelf}
							onPermissionDenied={onPermissionDenied}
						/>
					</div>
				</div>
			))}
		</div>
	);
};

export default RackVisual;
